import { Link } from 'react-router-dom';
import type { Cell } from '../demo/seed';
import { colorForTier } from '../lib/survival';
import { fmtTon, fmtCellId } from '../lib/format';

type Props = {
  cells: Cell[];
  rootId: number;
};

type TreeNode = {
  cell: Cell;
  children: TreeNode[];
};

function buildTree(cells: Cell[], rootId: number, depth = 0, max = 8): TreeNode | null {
  const cell = cells[rootId];
  if (!cell || !cell.agent) return null;
  if (depth > max) return { cell, children: [] };
  return {
    cell,
    children: cell.agent.childrenIds
      .map((cid) => buildTree(cells, cid, depth + 1, max))
      .filter((n): n is TreeNode => n !== null),
  };
}

function NodePill({ cell, highlight }: { cell: Cell; highlight: boolean }) {
  const color = colorForTier(cell.state);
  return (
    <Link
      to={`/lineage/${cell.id}`}
      className={`inline-flex flex-col items-start gap-0.5 hairline px-2 py-1 bg-panel hover:bg-void font-mono text-2xs ${
        highlight ? 'ring-1 ring-blue-glow' : ''
      }`}
      style={{ borderColor: color }}
    >
      <span className="text-text-primary">{cell.agent?.name ?? fmtCellId(cell.id)}</span>
      {cell.agent && (
        <span className="text-text-secondary">◊ {fmtTon(cell.agent.balance)} · g{cell.agent.generation}</span>
      )}
    </Link>
  );
}

function TreeView({ node, selectedId }: { node: TreeNode; selectedId: number }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <NodePill cell={node.cell} highlight={node.cell.id === selectedId} />
      {node.children.length > 0 && (
        <div className="ml-4 pl-4 border-l border-grid-line space-y-2">
          {node.children.map((child) => (
            <TreeView key={child.cell.id} node={child} selectedId={selectedId} />
          ))}
        </div>
      )}
    </div>
  );
}

function findRoot(cells: Cell[], id: number): number {
  let cur = cells[id];
  if (!cur || !cur.agent) return id;
  while (cur.agent && cur.agent.parentId != null) {
    cur = cells[cur.agent.parentId];
    if (!cur || !cur.agent) break;
  }
  return cur.id;
}

export function LineageTree({ cells, rootId }: Props) {
  const realRoot = findRoot(cells, rootId);
  const tree = buildTree(cells, realRoot);
  if (!tree) {
    return <div className="text-text-mono-dim text-xs p-4">No lineage data for this cell.</div>;
  }

  // collect all descendants of selected agent for the side list
  function collect(node: TreeNode, into: Cell[]): void {
    into.push(node.cell);
    node.children.forEach((c) => collect(c, into));
  }
  const all: Cell[] = [];
  collect(tree, all);

  const totalLineRevenue = all.reduce((sum, c) => sum + (c.agent?.revenue24h ?? 0), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      <div className="hairline p-4 bg-panel overflow-auto max-h-[70vh]">
        <TreeView node={tree} selectedId={rootId} />
      </div>
      <div className="hairline p-4 bg-panel space-y-3 max-h-[70vh] overflow-auto">
        <div className="text-text-mono-dim text-2xs uppercase">Descendants</div>
        <div className="text-xs">
          Total: <span className="font-mono">{all.length}</span> · 24h:{' '}
          <span className="font-mono text-green-thrive">◊ {fmtTon(totalLineRevenue)} TON</span>
        </div>
        <div className="space-y-2 text-2xs">
          {all.map((c) => {
            const pct = totalLineRevenue > 0 ? ((c.agent?.revenue24h ?? 0) / totalLineRevenue) * 100 : 0;
            return (
              <Link
                key={c.id}
                to={`/lineage/${c.id}`}
                className="flex items-center justify-between gap-2 hover:text-blue-life"
              >
                <span className="font-mono truncate">{c.agent?.name}</span>
                <span className="text-text-mono-dim font-mono">{pct.toFixed(1)}%</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
